import { notification } from 'antd';
import AppEvents from 'lib/AppEvents';
import Constants from 'lib/Constants';
import ApiService from 'services/ApiService';
import DataService from 'services/data/DataService';
import EventService from 'services/EventService';
import FacebookService from 'services/providers/FacebookService';
import Cookies from 'utils/Cookies';
import LocalStorage from 'utils/LocalStorage';
import { debug } from 'utils/debug';
import AccountDataService from 'services/data/AccountDataService';
import AdspendService from 'services/data/AdspendService';
import ApiService from 'services/ApiService';
import AppService from 'services/AppService';
import DataService from 'services/data/DataService';
import MetricService from 'services/data/MetricService';
import ShopifyService from 'services/providers/ShopifyService';
import TrackingService from 'services/tracking/TrackingService';
import NotificationService from './NotificationService';
import { getLogger } from '../utils/logging';

const { log, warn } = getLogger('AuthService', { color: 'yellow', enabled: false });

class AuthService {

    private AUTH_STATE_EVENT_NAME = "authstatechange";

    // loads auth0 and provider SDKs.
    // TODO: move this to each provider ...
    public init = (authOptions) => {
        return new Promise((resolve, reject) => {
            //return resolve(true);
            // todo: load dynamic library provider SDKs (FB, GA, etc).
            FacebookService.initialize(() => {
                return resolve(true);
            });
        });
    }

    public login = async (email, password, redirect, isMockAccount = false) => {
        return new Promise((resolve, reject) => {
            ApiService.post('/auth/login', { email, password, isMockAccount})
                .then(r => {
                    if (r.success) {
                        EventService.dispatch(AppEvents.LOGIN);
                        this.storeLoginResult(r);
                    }
                    resolve(r)
                })
                .catch(e => reject(e))
        });
    }

    // Makes request to backend for current user based on the token (ie. validates token)
    public checkSession = () => {
        return new Promise((resolve, reject) => {
            const token = Cookies.get(Constants.ACCESS_TOKEN);
            if (!token) return resolve(false);

            ApiService.post('/auth/check-session', { token })
                .then(async (r) => {
                    if (r.success) {
                        return resolve(r.user);
                    }
                    return resolve(r.success);
                })
                .catch(e => reject(e))
        });
    }

    public storeLoginResult = async (r) => {
        Cookies.set(Constants.ACCESS_TOKEN, r.token);
        LocalStorage.set(Constants.CURRENT_USER, r.user);

        if (r.isMockAccount && r.tokenOverrides) {
            //console.log('Overriding local tokens...', r.tokenOverrides)
            LocalStorage.set(Constants.TOKEN_OVERRIDES, r.tokenOverrides);
        } else if (r.isMockAccount) {
            console.error("WARNING: Found attempt to mock another account, but did not receive any token overrides in the login response. Cannot override tokens.");
        }
    };

    public clearLoginInfo = () => {
        Cookies.delete(Constants.ACCESS_TOKEN);
        LocalStorage.delete(Constants.CURRENT_USER);
        LocalStorage.delete(Constants.USE_MOCK_KEYS);
        LocalStorage.delete(Constants.TOKEN_OVERRIDES);
    };

    public getAccessToken = () => {
        return Cookies.get(Constants.ACCESS_TOKEN);
    }

    public getCurrentUser = () => {
        return LocalStorage.get(Constants.CURRENT_USER);
    }

    public updateCurrentUser = async (user) => {
        LocalStorage.set(Constants.CURRENT_USER, user);
        await DataService.saveDataSource('user', user);
    }

    public isAccountOwner = async (user) => {
        if (user) {
            // check if it's an account user, with role
            if (user.activeAccount && user.activeAccount.accountUsers) {
                const accountUsers = user.activeAccount.accountUsers.filter(au => au.email === user.email);
                if (accountUsers.length > 0) return false;
            }
        }

        // true if they are not in the subusers
        log(`isAccountOwner = true`, user);
        return true;
    }

    public logout = async (redirect) => {
        this.clearLoginInfo();
        let user = this.getCurrentUser();
        if (user) {
            debug(user, "Logging out, and clearing all service data.");
        }

        // manually "logout" of all services (ie. cleanup data);
        //console.log('Clearing data for all services.');
        await this.clearData();
        EventService.dispatch(AppEvents.LOGOUT, { redirect });
    }

    public clearData = async () => {
        await Promise.allSettled([
            AccountDataService.clearData,
            AdspendService.clearData,
            AppService.clearData,
            DataService.clearData,
            MetricService.clearData,
            ShopifyService.clearData,
            TrackingService.clearData
        ]);
    }

    public signup = async (profile, goals, paymentMethod, fbAccount, fbToken, fbPixel, fbUserId?) => {
        return new Promise((resolve, reject) => {
            ApiService.post('/auth/signup', { profile, goals, paymentMethod, fbAccount, fbToken, fbPixel, fbUserId })
                .then(r => {
                    resolve(r)
                })
                .catch(e => reject(e))
        });
    }

    public acceptInvite = async (token, name, password) => {
        return new Promise((resolve, reject) => {
            ApiService.post('/auth/accept-invite', {token, name, password})
                .then((r) => {
                    resolve(r);
                })
                .catch((e) => {
                    if (e.response) {
                        reject(e.response.data);
                    } else {
                        reject(e);
                    }
                });
        });
    };

    public forgotPassword = async (email) => {
        return new Promise((resolve, reject) => {
            ApiService.post('/auth/forgot-password', {email})
                .then((r) => {
                    resolve(r);
                })
                .catch((e) => {
                    if (e.response) {
                        reject(e.response.data);
                    } else {
                        reject(e);
                    }
                });
        });
    };

    public resetPassword = async (token, password) => {
        return new Promise((resolve, reject) => {
            ApiService.post('/auth/reset-password', {token, password})
                .then((r) => {
                    resolve(r);
                })
                .catch((e) => {
                    if (e.response) {
                        reject(e.response.data);
                    } else {
                        reject(e);
                    }
                });
        });
    };

    public checkEmailExists = async (email) => {
        return new Promise((resolve, reject) => {
            ApiService.post('/auth/check-email-exists', { email })
                .then(r => {
                    if (r.success & r.exists) {
                        return resolve(true);
                    }

                    return resolve(false);
                })
                .catch(e => reject(e))
        });
    }

    public changeActiveAccount = async (toAccountId) => {
        return new Promise((resolve, reject) => {
            ApiService.post('/users/change-active-account', { toAccountId })
                .then(r => {
                    if (r.success) {
                        return resolve(r.user);
                    }
                    return resolve(r.null);
                })
                .catch(e => reject(e));
        });
    }

    // todo: refactor below here, used by admin only /////////////////////////////////////////

    public onAuthStateChanged(cb) {
        function eCB(e: CustomEvent) {
            cb(e.detail);
        }
        window.addEventListener(this.AUTH_STATE_EVENT_NAME, eCB);
        return () => {
            window.removeEventListener(this.AUTH_STATE_EVENT_NAME, eCB);
        }
    }

    reloadUserInfo() {
        return this.authenticateWithToken();
    }

    authenticateWithToken() {
        const token = this.getAccessToken();
        if (!token) {
            return Promise.reject();
        }
        return APIClient.post('/auth/decode-token', { token }, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(res => {
            return this._handleLoginResponse(res, true);
        });
    }

    private async _handleLoginResponse(response, dontEvaluateSuccessResponse = false) {
        if (dontEvaluateSuccessResponse) {
            if (response.data.decoded.email) {
                const token = Cookies.get(Constants.ACCESS_TOKEN);
                const user = this.getUser();
                this.triggerAuthStateChange({ ok: true, user, token })
                return { ok: true, user, token };
            } else {
                // bad tokens
                return await this.logout();
            }
        } else if (!response.data.success || !response.data.token) {
            if (response.data.error) {
                NotificationService.notifyError('Authentication Error',
                    typeof response.data.error === "string" ? response.data.error : "Authentication Failed");
            }

            await this.logout();
            this.triggerAuthStateChange({ ok: false, user: null, token: '' });
            return { ok: false, user: null, token: '' };
        }

        this.storeLoginResult(response.data);

        // ask for full user object:
        // await this.getCurrentUser(true);

        NotificationService.notifySuccess('Success', 'You are logged in!');

        this.triggerAuthStateChange({ ok: true, user: response.data.user, token: response.data.token });

        return { ok: true, user: response.data.user, token: response.data.token };
    }


}

export default new AuthService();